import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, REGIMI } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { denominazione: "", piva: "", email: "", telefono: "", comune: "", active: true };
const ROW = "grid items-center gap-2 border-b border-slate-100 py-2.5 sm:grid-cols-[220px_1fr]";

const Row = ({ label, children }) => <div className={ROW}><span className="text-sm text-slate-600">{label}</span>{children}</div>;

export const StudioDati = ({ studio, onSaved, onDeleted }) => {
  const [s, setS] = useState({ ...EMPTY, ...studio });
  const [acc, setAcc] = useState({ email: "", password: "", active: true });
  useEffect(() => {
    setS({ ...EMPTY, ...studio });
    setAcc({ email: "", password: "", active: true });
    if (studio.id) api.get(`/admin/studios/${studio.id}/accesso`).then((r) => setAcc({ email: r.data.email || "", password: "", active: r.data.active ?? true, exists: !!r.data.id }));
  }, [studio]);
  const save = async () => {
    try {
      const { data } = s.id ? await api.put(`/admin/studios/${s.id}`, s) : await api.post("/admin/studios", s);
      if (acc.email) {
        const r = await api.put(`/admin/studios/${data.id}/accesso`, { email: acc.email, password: acc.password, active: acc.active });
        if (r.data.unito) toast.success("Accesso unito: con questa email o con la partita IVA dell'azienda si entra come studio, e l'azienda è abbinata allo studio", { duration: 8000 });
      }
      toast.success(acc.email ? "Studio e accesso salvati" : "Studio salvato");
      onSaved(data.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.existing_id) { toast.warning(d.msg, { duration: 8000 }); onSaved(d.existing_id); return; }
      toast.error(errMsg(e));
    }
  };
  const f = (k, l) => <Row label={l}><Input data-testid={`studio-${k}-input`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></Row>;
  return (
    <div data-testid="studio-dati">
      {f("denominazione", "Ragione sociale")}{f("piva", "P.IVA")}{f("comune", "Comune")}{f("email", "Email dello studio")}{f("telefono", "Telefono")}
      <Row label="Lo studio è anche azienda"><label className="flex items-center gap-2 text-sm"><Checkbox data-testid="studio-anche-azienda-checkbox" checked={!!s.anche_azienda} onCheckedChange={(v) => setS({ ...s, anche_azienda: !!v })} />Sì, fattura con la propria P.IVA (compare "Gestisci fatturazione")</label></Row>
      {s.anche_azienda && (
        <div data-testid="studio-azienda-dati">
          <div className="mt-5 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Dati per fatturare (azienda dello studio)</div>
          {f("indirizzo", "Indirizzo")}{f("cap", "CAP")}{f("provincia", "Provincia (sigla)")}{f("pec", "PEC")}
          <Row label="Regime fiscale">
            <Select value={s.regime_fiscale || "RF01"} onValueChange={(v) => setS({ ...s, regime_fiscale: v })}>
              <SelectTrigger data-testid="studio-regime-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(REGIMI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
        </div>
      )}
      <div className="mt-5 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Accesso al gestionale</div>
      <Row label="Login (email di accesso)"><Input type="email" data-testid="studio-login-input" placeholder="es. studio@dominio.it" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} /></Row>
      <Row label={acc.exists ? "Nuova password (vuota = invariata)" : "Password (min. 8)"}><Input type="password" autoComplete="new-password" data-testid="studio-password-input" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} /></Row>
      <Row label="Attivo"><label className="flex items-center gap-2 text-sm"><Checkbox data-testid="studio-active-checkbox" checked={acc.active} onCheckedChange={(v) => setAcc({ ...acc, active: !!v })} />Sì</label></Row>
      <div className="mt-6 flex justify-center gap-3">
        <Button data-testid="save-studio-button" className="bg-sky-600 hover:bg-sky-700" disabled={!s.denominazione} onClick={save}>{s.id ? "Modifica" : "Crea studio"}</Button>
        {s.id && <ConfirmDelete testid={`delete-studio-${s.id}`} label="Cancella" text="Le aziende abbinate resteranno senza studio e gli utenti dello studio verranno eliminati." onConfirm={async () => { await api.delete(`/admin/studios/${s.id}`); onDeleted(); }} />}
      </div>
    </div>
  );
};
